import styles from "./addJsonFieldModal.module.scss";
import { ModalWindow } from "../../modalWindow/modalWindow";
import { useTranslation } from "react-i18next";
import { JsonField } from "../../../../types/jsonValidationTypes";
import {
  useCallback,
  useMemo
} from "react";
import { Input } from "../../input/input";
import {
  boolean,
  number,
  object,
  string
} from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dropdown,
  DropdownItem
} from "../../dropdown/dropdown";
import { useGetAllValidationTypeQuery } from "../../../../api/jsonValidationService";
import { SwitchCheckboxHookForm } from "../../switchCheckboxHookForm/switchCheckboxHookForm";

type AddJsonFieldModalProps = {
  onClose: (field?: JsonField) => void;
}

export const AddJsonFieldModal = ({ onClose }: AddJsonFieldModalProps) => {
  const { t } = useTranslation();

  const { data: jsonTypes } = useGetAllValidationTypeQuery("");

  const schema = object({
    name: string().required(t("common.notValid")).typeError(t("common.notValid")),
    type: number().required(),
    size: number().nullable().transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    }),
    required: boolean().required(),
  });

  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { required: true },
    mode: "onChange"
  });

  const onClickSave = useCallback(() => {
    handleSubmit((result) => {
      const type = jsonTypes?.find(item => item.id === result.type);
      if (!!type) {
        onClose({
          name: result.name,
          size: result.size,
          type: type.name,
          required: result.required
        });
      } else {
        console.error("Error find type");
      }
    })();
  }, [handleSubmit, onClose, jsonTypes]);

  const typeDropdownItems: Array<DropdownItem> = useMemo(() => {
    return jsonTypes?.map(item => ({
      id: item.id,
      name: item.name,
      title: item.name
    })) || []
  }, [jsonTypes]);

  return (
    <ModalWindow
      title={t("parameters.addJsonField")}
      onClose={() => onClose()}
      width={380}
      children={
        <div className={styles.container}>
          <div className={styles.inputContainer}>
            <span>{t("common.name")}</span>
            <div className={styles.input}>
              <Input control={control}
                     name={"name"}
                     maxLength={100}
                     error={!!errors.name ? errors.name.message : ""}/>
            </div>
          </div>
          <div className={styles.inputContainer}>
            <span>{t("parameters.type")}</span>
            <div className={styles.input}>
              <Dropdown control={control}
                        name={"type"}
                        fontPopupClass={styles.dropdownFont}
                        selectedItemClass={styles.dropdownFont}
                        items={typeDropdownItems}
                        error={!!errors.type ? errors.type.message : ""}/>
            </div>
          </div>
          <div className={styles.inputContainer}>
            <span>{t("parameters.size")}</span>
            <div className={styles.input}>
              <Input control={control}
                     name={"size"}
                     type={"number"}
                     error={!!errors.size ? errors.size.message : ""}/>
            </div>
          </div>
          <div className={styles.checkboxContainer}>
            <span>{t("common.required")}</span>
            <SwitchCheckboxHookForm control={control}
                                    name={"required"}/>
          </div>
          <div className={styles.buttonsContainer}>
            <button className={styles.cancel}
                    onClick={() => onClose()}>{t("common.cancel")}</button>
            <button className={styles.save}
                    onClick={onClickSave}>{t("common.save")}</button>
          </div>
        </div>
      }/>
  );
}